import type {
  LoaderFunctionArgs,
  MetaDescriptor,
  MetaFunction,
} from "react-router";
import { OG_IMAGE_SLOT } from "@diality/contracts";
import { resolveApiEndpoint } from "./api-endpoint";
import { resolveMarketingMedia } from "./block-render";
import { fetchPageList } from "./page-loader";
import type { MarketingPageLoaderData } from "./page-loader";
import {
  CANONICAL_ORIGIN,
  isAdminPath,
  publicSiteIsIndexable,
  robotsTxt,
} from "./crawl-policy";
export { CANONICAL_ORIGIN } from "./crawl-policy";

/**
 * Crawler-facing discovery surface: robots.txt and the XML sitemap.
 *
 * Non-production environments block crawling. Production allows the public
 * site while excluding the CMS, whose HTML and responses also carry noindex.
 */
export async function robotsTxtLoader(_args: LoaderFunctionArgs) {
  const body = robotsTxt(publicSiteIsIndexable());

  return new Response(body, {
    headers: { "Content-Type": "text/plain; charset=utf-8" },
  });
}

/** Escape a URL for XML text content. */
function xmlEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * The XML sitemap: every currently published page (a page is published when it
 * has a live version). Generated from the live page list on each request, so
 * drafts never appear and retired pages drop out as soon as they are
 * unpublished — nothing is hand-maintained. When the API is unreachable the
 * sitemap degrades to an empty urlset rather than an error page.
 */
export async function sitemapLoader({ request }: LoaderFunctionArgs) {
  // Generate the same document in every environment so authenticated dev
  // reviewers can inspect it. Crawling policy and the edge access gate are
  // independent: dev remains disallowed/noindex and requires Basic Auth.
  const endpoint = await resolveApiEndpoint({
    baseUrl: request.url,
    signal: request.signal,
  });
  const pages =
    (await fetchPageList(endpoint, request))?.filter(
      (page) =>
        page.liveVersionId &&
        !isAdminPath(new URL(page.url, CANONICAL_ORIGIN).pathname),
    ) ?? [];

  const urls = pages
    .map(
      (page) =>
        `  <url><loc>${xmlEscape(`${CANONICAL_ORIGIN}${page.url}`)}</loc></url>`,
    )
    .join("\n");
  const body = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    urls,
    "</urlset>",
    "",
  ].join("\n");

  return new Response(body, {
    headers: {
      "Content-Type": "application/xml; charset=utf-8",
    },
  });
}

/**
 * NewsArticle structured data (schema.org JSON-LD) for a published press
 * release, derived from the article's content: the headline is the
 * ArticleHeader block's heading and datePublished its editorial date (with
 * the page title / publish stamp as fallbacks for older content). Leahi is
 * not FDA-cleared, so this carries no efficacy/performance claims —
 * aspirational framing only.
 */
export function articleStructuredData(article: {
  headline: string;
  datePublished: string | null;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.headline,
    ...(article.datePublished ? { datePublished: article.datePublished } : {}),
  };
}

/**
 * Organization structured data (schema.org JSON-LD) — the same blob on every
 * page, so platforms always read consistent identity. The URL is the canonical
 * production origin: it identifies the organization, not the deployment that
 * served the page. No product-claim language (Leahi is not FDA-cleared).
 */
export function organizationStructuredData(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Diality",
    url: CANONICAL_ORIGIN,
    description:
      "Diality is reimagining hemodialysis — engineered around how dialysis actually works.",
  };
}

/**
 * Head tags for a marketing route. A CMS-published page renders its SEO
 * metadata — title, meta description, Open Graph — read from the unversioned
 * record, so a Manager's edit is reflected on the next request without a
 * publish step. Static fallback pages keep the site default title.
 */
export const marketingMeta: MetaFunction = ({ loaderData }) => {
  const data = loaderData as MarketingPageLoaderData | undefined;
  if (data?.kind !== "cms") return [{ title: "Diality" }];

  const { page, seo } = data;
  const title = seo?.title || page.title;
  const description = seo?.description ?? "";
  const ogTitle = seo?.ogTitle || title;
  const ogDescription = seo?.ogDescription || description;

  const tags: MetaDescriptor[] = [{ title }];
  if (description) tags.push({ name: "description", content: description });
  if (ogTitle) tags.push({ property: "og:title", content: ogTitle });
  if (ogDescription)
    tags.push({ property: "og:description", content: ogDescription });

  // The share image. resolveMarketingMedia returns null for a reference that
  // cannot name its photo, so a half-formed ref publishes no tag rather than a
  // URL with a hole in it. Its path is site-relative; og:image must be
  // absolute — platforms fetch it without a page to resolve against — so it is
  // qualified with the same canonical origin the organization data uses.
  const shareImage = resolveMarketingMedia(seo?.ogImage ?? null);
  if (shareImage) {
    tags.push({
      property: "og:image",
      content: `${CANONICAL_ORIGIN}${shareImage.url}`,
    });
    // Declared so a platform can lay the card out before the bytes arrive. The
    // dimensions are the slot's, which is what the crop was framed to.
    tags.push({
      property: "og:image:width",
      content: String(OG_IMAGE_SLOT.width),
    });
    tags.push({
      property: "og:image:height",
      content: String(OG_IMAGE_SLOT.height),
    });
  }
  return tags;
};
